import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Can from '../../components/Can';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Typography,
  TablePagination,
  TextField,
  useTheme,
  useMediaQuery,
  Stack,
  InputAdornment,
  Tooltip,
  Grid,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import remitoCompraService, { RemitoCompra, RemitoCompraSearchParams } from '../../services/remitoCompraService';
import comprasSeguimientoService, { CompraPendienteMaster } from '../../services/comprasSeguimientoService';
import { Proveedor } from '../../services/proveedorService';
import ProveedorSearch from '../../components/ProveedorSearch';
import { formatDate, formatProveedorConNro } from '../../utils/formatters';
import { getApiErrorMessage } from '../../utils/apiError';
import { esRemitoCompraAnulado, remitoPuedeAnular } from '../../utils/remitoPuedeAnular';
import {
  RemitoCompraEstadoFacturacion,
  buildPendientesPorProveedor,
  calcularEstadoFacturacionRemito,
} from '../../utils/remitoCompraFacturacion';

const getFirstDayOfMonth = (): string => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().split('T')[0];
};

const getLastDayOfMonth = (): string => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return d.toISOString().split('T')[0];
};

const formatearFecha = (fecha: Date | string | null | undefined): string => {
  if (!fecha) return '-';
  const d = typeof fecha === 'string' ? new Date(fecha) : fecha;
  if (isNaN(d.getTime())) return '-';
  return formatDate(d.toISOString().split('T')[0]);
};

/** Formato Comprobante: A-0001-00000001 */
const formatoComprobante = (letra?: string, nroSuc?: number, nroComprobante?: number): string => {
  const L = (letra ?? '').trim() || '-';
  const suc = String(nroSuc ?? 0).padStart(4, '0');
  const nro = String(nroComprobante ?? 0).padStart(8, '0');
  return `${L}-${suc}-${nro}`;
};

const parseNroComprobante = (r: RemitoCompra): number => {
  if (typeof r.nroComprobante === 'number') return r.nroComprobante;
  const nroRemito = parseInt(r.nroRemito ?? '', 10);
  return Number.isNaN(nroRemito) ? 0 : nroRemito;
};

const RemitosCompra: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [remitos, setRemitos] = useState<RemitoCompra[]>([]);
  const [estadoFacturacion, setEstadoFacturacion] = useState<Record<number, RemitoCompraEstadoFacturacion>>({});
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [anulandoId, setAnulandoId] = useState<number | null>(null);
  const [facturandoId, setFacturandoId] = useState<number | null>(null);
  const [openProveedorSearch, setOpenProveedorSearch] = useState(false);
  const [nombreProveedorFiltro, setNombreProveedorFiltro] = useState('');

  const [filters, setFilters] = useState(() => {
    const desde = searchParams.get('fechaDesde');
    const hasta = searchParams.get('fechaHasta');
    return {
      searchTerm: '',
      fechaDesde: desde && /^\d{4}-\d{2}-\d{2}$/.test(desde) ? desde : getFirstDayOfMonth(),
      fechaHasta: hasta && /^\d{4}-\d{2}-\d{2}$/.test(hasta) ? hasta : getLastDayOfMonth(),
      nroProveedor: '',
      nroSuc: '',
    };
  });

  const fetchRemitos = useCallback(
    async (pageNum: number = page, pageSize: number = rowsPerPage, filtros: typeof filters = filters) => {
      try {
        setLoading(true);
        const params: RemitoCompraSearchParams = {
          pageNumber: pageNum + 1,
          pageSize,
          searchTerm: filtros.searchTerm || undefined,
          fechaDesde: filtros.fechaDesde || undefined,
          fechaHasta: filtros.fechaHasta || undefined,
          nroProveedor: filtros.nroProveedor || undefined,
          nroSuc: filtros.nroSuc ? parseInt(filtros.nroSuc, 10) : undefined,
        };
        const result = await remitoCompraService.getRemitosCompra(params);
        const items = result.items || [];
        setRemitos(items);
        setTotalCount(result.totalCount || 0);

        const proveedores = items
          .map((r) => r.nroCliente)
          .filter((n): n is number => typeof n === 'number' && n > 0);
        const pendientesPorProveedor = await buildPendientesPorProveedor(proveedores, (nroProveedor) =>
          comprasSeguimientoService.getPendientesMaster(nroProveedor, false)
        );
        const estados: Record<number, RemitoCompraEstadoFacturacion> = {};
        for (const r of items) {
          const pendMap =
            r.nroCliente && r.nroCliente > 0
              ? pendientesPorProveedor.get(r.nroCliente) ?? new Map()
              : new Map();
          estados[r.nroFormulario] = calcularEstadoFacturacionRemito(r, pendMap);
        }
        setEstadoFacturacion(estados);
      } catch (error: unknown) {
        console.error('Error al cargar remitos de compra:', error);
        const msg =
          error && typeof error === 'object' && 'response' in error
            ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
            : 'Error al cargar remitos de compra';
        enqueueSnackbar(msg || 'Error al cargar remitos de compra', { variant: 'error' });
        setRemitos([]);
        setEstadoFacturacion({});
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar]
  );

  useEffect(() => {
    fetchRemitos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Sincronizar fechas desde URL cuando cambian los searchParams (ej. al llegar desde dashboard)
    const desde = searchParams.get('fechaDesde');
    const hasta = searchParams.get('fechaHasta');
    setFilters((f) => ({
      ...f,
      ...(desde && /^\d{4}-\d{2}-\d{2}$/.test(desde) ? { fechaDesde: desde } : {}),
      ...(hasta && /^\d{4}-\d{2}-\d{2}$/.test(hasta) ? { fechaHasta: hasta } : {}),
    }));
    setPage(0);
  }, [searchParams]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(0);
      fetchRemitos(0, rowsPerPage, filters);
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [filters]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    fetchRemitos(newPage, rowsPerPage, filters);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchRemitos(0, newRowsPerPage, filters);
  };

  const handleBuscar = () => {
    setPage(0);
    fetchRemitos(0, rowsPerPage, filters);
  };

  const handleProveedorSelect = (proveedor: Proveedor) => {
    setFilters((f) => ({ ...f, nroProveedor: String(proveedor.nroProveedor) }));
    setNombreProveedorFiltro((proveedor.nombre ?? '').trim());
  };

  const getEstadoFacturacion = (remito: RemitoCompra): RemitoCompraEstadoFacturacion => {
    return (
      estadoFacturacion[remito.nroFormulario] ?? {
        puedeFacturar: !remito.nroFormularioFactura || remito.nroFormularioFactura <= 0,
        facturadoCompleto: !!remito.nroFormularioFactura && remito.nroFormularioFactura > 0,
        facturadoParcial: false,
        ultimaFactura: remito.nroFormularioFactura ?? undefined,
      }
    );
  };

  const tooltipFacturacion = (remito: RemitoCompra): string => {
    const est = getEstadoFacturacion(remito);
    if (est.facturadoCompleto) {
      return est.ultimaFactura ? `Ya facturado (Factura ${est.ultimaFactura})` : 'Ya facturado';
    }
    if (est.facturadoParcial && est.ultimaFactura) {
      return `Facturado parcial — pendiente de facturar (Factura ${est.ultimaFactura})`;
    }
    return 'Facturar';
  };

  const handleFacturar = async (remito: RemitoCompra, event: React.MouseEvent) => {
    event.stopPropagation();
    if (facturandoId != null) return;
    if (esRemitoCompraAnulado(remito)) {
      enqueueSnackbar('No se puede facturar un remito anulado', { variant: 'warning' });
      return;
    }
    try {
      setFacturandoId(remito.nroFormulario);
      const detalle = await remitoCompraService.getRemitoCompra(remito.nroFormulario);
      if (esRemitoCompraAnulado(detalle)) {
        enqueueSnackbar('No se puede facturar un remito anulado', { variant: 'warning' });
        return;
      }
      let pendMap = new Map<string, CompraPendienteMaster>();
      if (detalle.nroCliente && detalle.nroCliente > 0) {
        try {
          const pendientes = await comprasSeguimientoService.getPendientesMaster(detalle.nroCliente, false);
          pendMap = new Map(
            (pendientes || []).map((p) => [`${p.nroFormularioMaster}-${p.nroItemMaster}`, p])
          );
        } catch {
          /* se usa cantidad del remito si no hay seguimiento */
        }
      }
      const est = calcularEstadoFacturacionRemito(detalle, pendMap);
      if (!est.puedeFacturar) {
        enqueueSnackbar('El remito no tiene cantidades pendientes de facturar', { variant: 'info' });
        return;
      }
      navigate(`/compras/facturas/nuevo?remitoId=${remito.nroFormulario}`);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo validar el remito para facturar'), { variant: 'error' });
    } finally {
      setFacturandoId(null);
    }
  };

  const handleAnular = async (remitoId: number, event?: React.MouseEvent) => {
    event?.stopPropagation();
    if (anulandoId != null) return;
    try {
      const detalle = await remitoCompraService.getRemitoCompra(remitoId);
      if (esRemitoCompraAnulado(detalle)) {
        enqueueSnackbar('Este remito ya está anulado', { variant: 'warning' });
        return;
      }
      const check = await remitoPuedeAnular(detalle.nroFormulario, detalle.detalles);
      if (!check.puedeAnular) {
        enqueueSnackbar(check.motivo, { variant: 'warning' });
        return;
      }
      if (!window.confirm('¿Está seguro que desea anular este remito de compra?')) return;

      setAnulandoId(remitoId);
      await remitoCompraService.anularRemitoCompra(remitoId);
      enqueueSnackbar('Remito de compra anulado correctamente', { variant: 'success' });
      await fetchRemitos(page, rowsPerPage, filters);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al anular el remito de compra'), { variant: 'error' });
    } finally {
      setAnulandoId(null);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Remitos de Compra
            </Typography>
            <Can permiso="compras.remitos.nuevo">
            <Button
              variant="contained"
              sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}
              startIcon={<AddIcon />}
              onClick={() => navigate('/compras/remitos/nuevo')}
            >
              Nuevo Remito
            </Button>
            </Can>
          </Stack>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Nro. formulario, fecha, comprobante o proveedor…"
                value={filters.searchTerm}
                onChange={(e) => setFilters((f) => ({ ...f, searchTerm: e.target.value }))}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#1976d2' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Fecha desde"
                value={filters.fechaDesde}
                onChange={(e) => setFilters((f) => ({ ...f, fechaDesde: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Fecha hasta"
                value={filters.fechaHasta}
                onChange={(e) => setFilters((f) => ({ ...f, fechaHasta: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={1}>
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="Suc"
                value={filters.nroSuc}
                onChange={(e) => setFilters((f) => ({ ...f, nroSuc: e.target.value }))}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Nro. proveedor"
                placeholder="Ej: 1001"
                value={filters.nroProveedor}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '');
                  setFilters((f) => ({ ...f, nroProveedor: v }));
                  setNombreProveedorFiltro('');
                }}
                inputProps={{ inputMode: 'numeric', 'aria-label': 'Número de proveedor' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Buscar proveedor por nombre">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => setOpenProveedorSearch(true)}
                          aria-label="Buscar proveedor por nombre"
                          sx={{ color: '#1976d2' }}
                        >
                          <SearchIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                helperText={nombreProveedorFiltro ? `Nombre: ${nombreProveedorFiltro}` : undefined}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="outlined"
                onClick={handleBuscar}
                sx={{ borderRadius: 2, color: '#1976d2', borderColor: '#1976d2' }}
              >
                Buscar
              </Button>
            </Grid>
          </Grid>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress sx={{ color: '#1976d2' }} />
            </Box>
          ) : isMobile ? (
            <>
              <Stack spacing={2}>
                {remitos.map((r) => (
                  <Card key={r.nroFormulario} variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {formatoComprobante(r.letra, r.nroSuc, parseNroComprobante(r))}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Fecha: {formatearFecha(r.fechaEmision)} | Proveedor: {formatProveedorConNro(r.nroCliente, r.nombre)}
                      </Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                        {!esRemitoCompraAnulado(r) &&
                          (getEstadoFacturacion(r).puedeFacturar ? (
                          <Can permiso="compras.facturas.nuevo">
                          <Tooltip title={tooltipFacturacion(r)}>
                            <span>
                            <IconButton
                              size="small"
                              onClick={(event) => void handleFacturar(r, event)}
                              disabled={facturandoId === r.nroFormulario}
                              sx={{ color: '#1976d2' }}
                            >
                              {facturandoId === r.nroFormulario ? (
                                <CircularProgress size={18} color="inherit" />
                              ) : (
                                <ReceiptIcon />
                              )}
                            </IconButton>
                            </span>
                          </Tooltip>
                          </Can>
                        ) : (
                          <Tooltip title={tooltipFacturacion(r)}>
                            <span>
                              <IconButton
                                size="small"
                                disabled
                                sx={{
                                  color: '#2e7d32',
                                  '&.Mui-disabled': { color: '#2e7d32', opacity: 1 },
                                }}
                              >
                                <CheckCircleIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ))}
                        <Tooltip title="Ver">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/compras/remitos/${r.nroFormulario}`)}
                            sx={{ color: '#1976d2' }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        {!esRemitoCompraAnulado(r) && (
                          <Tooltip title="Anular">
                            <span>
                              <IconButton
                                size="small"
                                onClick={(event) => void handleAnular(r.nroFormulario, event)}
                                disabled={anulandoId === r.nroFormulario}
                                sx={{ color: 'error.main' }}
                              >
                                {anulandoId === r.nroFormulario ? (
                                  <CircularProgress size={18} color="inherit" />
                                ) : (
                                  <BlockIcon />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </Stack>
                  </Card>
                ))}
              </Stack>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 20]}
                labelRowsPerPage="Filas:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : to}`}
                sx={{ mt: 2 }}
              />
            </>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 600, display: { xs: 'none', lg: 'table-cell' }, minWidth: 160, whiteSpace: 'nowrap' }}>Comprobante</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Proveedor</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {remitos.map((r) => (
                    <TableRow key={r.nroFormulario} hover>
                      <TableCell>{formatearFecha(r.fechaEmision)}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, whiteSpace: 'nowrap' }}>{formatoComprobante(r.letra, r.nroSuc, parseNroComprobante(r))}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{formatProveedorConNro(r.nroCliente, r.nombre)}</TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0} alignItems="center" justifyContent="center" sx={{ width: '100%' }}>
                          {!esRemitoCompraAnulado(r) &&
                            (getEstadoFacturacion(r).puedeFacturar ? (
                            <Can permiso="compras.facturas.nuevo">
                            <Tooltip title={tooltipFacturacion(r)}>
                              <span>
                              <IconButton
                                size="small"
                                onClick={(event) => void handleFacturar(r, event)}
                                disabled={facturandoId === r.nroFormulario}
                                sx={{ color: '#1976d2' }}
                              >
                                {facturandoId === r.nroFormulario ? (
                                  <CircularProgress size={18} color="inherit" />
                                ) : (
                                  <ReceiptIcon />
                                )}
                              </IconButton>
                              </span>
                            </Tooltip>
                            </Can>
                          ) : (
                            <Tooltip title={tooltipFacturacion(r)}>
                              <span>
                                <IconButton
                                  size="small"
                                  disabled
                                  sx={{
                                    color: '#2e7d32',
                                    '&.Mui-disabled': { color: '#2e7d32', opacity: 1 },
                                  }}
                                >
                                  <CheckCircleIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          ))}
                          <Tooltip title="Ver">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/compras/remitos/${r.nroFormulario}`)}
                              sx={{ color: '#1976d2' }}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          {!esRemitoCompraAnulado(r) && (
                            <Tooltip title="Anular">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={(event) => void handleAnular(r.nroFormulario, event)}
                                  disabled={anulandoId === r.nroFormulario}
                                  sx={{ color: 'error.main' }}
                                >
                                  {anulandoId === r.nroFormulario ? (
                                    <CircularProgress size={18} color="inherit" />
                                  ) : (
                                    <BlockIcon />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 20]}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <ProveedorSearch
        open={openProveedorSearch}
        onClose={() => setOpenProveedorSearch(false)}
        onSelect={handleProveedorSelect}
      />
    </Box>
  );
};

export default RemitosCompra;

